import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, ReceiptText, ScrollText, Wallet } from "lucide-react";
import { useDocuments, useSettings, useStats } from "@/hooks/useData";
import { useAuth } from "@/lib/auth";
import { DOC_TYPES, fmtDate, fmtMoney } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { OverdueBanner } from "@/components/Reminders";

const icons = { invoice: FileText, quotation: ScrollText, receipt: ReceiptText, voucher: Wallet };

export default function Dashboard() {
  const { user } = useAuth();
  const { data: stats } = useStats();
  const { data: settings } = useSettings();
  const { data: docs = [] } = useDocuments({});

  return (
    <div className="rise space-y-10" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="overline mb-3">{settings?.company_name || "Workspace"}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Good to see you, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <div className="panel p-6 min-w-[240px]">
          <div className="overline mb-2">Outstanding invoices</div>
          <div className="font-mono text-3xl font-semibold" data-testid="stat-outstanding">{fmtMoney(stats?.outstanding, settings)}</div>
        </div>
      </div>

      <OverdueBanner />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-border divide-y md:divide-y-0 md:divide-x divide-border">
        {Object.entries(DOC_TYPES).map(([type, meta]) => {
          const Icon = icons[type];
          const s = stats?.by_type?.[type];
          return (
            <Link key={type} to={`/documents/new?type=${type}`} data-testid={`create-${type}-card`} className="group p-8 bg-card hover:bg-muted transition-colors relative">
              <Icon className="w-5 h-5 mb-8" strokeWidth={1.5} />
              <div className="overline mb-1">{meta.plural}</div>
              <div className="font-mono text-3xl font-semibold">{s?.count ?? 0}</div>
              <div className="font-mono text-xs text-muted-foreground mt-1">{fmtMoney(s?.total, settings)}</div>
              <div className="absolute top-8 right-8 flex items-center gap-1 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                New <ArrowUpRight className="w-4 h-4" />
              </div>
            </Link>
          );
        })}
      </div>

      <section className="panel">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">Recent documents</h2>
          <Link to="/documents" className="text-sm font-semibold hover:text-primary transition-colors" data-testid="view-all-documents">View all</Link>
        </div>
        {docs.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <p className="mb-4">No documents yet. Start with your first invoice or quotation.</p>
            <Link to="/documents/new" data-testid="empty-create-button" className="btn-press inline-flex h-10 px-5 items-center bg-primary text-primary-foreground font-semibold text-sm">Create a document</Link>
          </div>
        ) : (
          <ul>
            {docs.slice(0, 8).map((d) => (
              <li key={d.id} className="border-b border-border/40 last:border-0">
                <Link to={`/documents/${d.id}`} data-testid={`recent-doc-${d.id}`} className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-muted transition-colors">
                  <div className="col-span-5 sm:col-span-3 font-mono text-sm">{d.number}</div>
                  <div className="col-span-7 sm:col-span-4 text-sm truncate">{d.party?.name || "—"}</div>
                  <div className="hidden sm:block col-span-2 text-xs text-muted-foreground">{fmtDate(d.issue_date)}</div>
                  <div className="col-span-6 sm:col-span-1"><StatusBadge status={d.status} /></div>
                  <div className="col-span-6 sm:col-span-2 text-right font-mono text-sm">{fmtMoney(d.total, settings)}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
